import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import { Euler, Matrix4, Quaternion, Vector3 } from 'three';
// Scratch objects — safe to share across instances since useFrame is synchronous
export const useBillboardObject3d = (args) => {
    const { object3d, rotation, follow, lockY, isCameraOrbiting, billboardQuatRef } = args;
    const prevRotationRef = useRef(undefined);
    const cameraPositionCachedRef = useRef(false);
    const prevCameraOrbitingRef = useRef(isCameraOrbiting);
    const scratchRef = useRef({
        objectEuler: new Euler(),
        parentEuler: new Euler(),
        worldPos: new Vector3(),
        cameraWorldPosition: new Vector3(),
        lookAtMatrix: new Matrix4(),
        userQuat: new Quaternion(),
        parentQuatInv: new Quaternion(),
        upVector: new Vector3(0, 1, 0),
        defaultBillboardQuat: new Quaternion(),
        cameraUpVector: new Vector3(0, 1, 0),
    });
    useFrame(({ camera }) => {
        const obj = object3d.current;
        if (!obj)
            return;
        // Per-instance scratch objects
        const { objectEuler, parentEuler, worldPos, cameraWorldPosition, lookAtMatrix, userQuat, parentQuatInv, upVector, defaultBillboardQuat, cameraUpVector, } = scratchRef.current;
        // If not following, apply only user rotation and exit
        if (!follow) {
            if (rotation)
                obj.rotation.set(...rotation);
            return;
        }
        const billboardQuat = billboardQuatRef?.current ?? defaultBillboardQuat;
        // Compute user rotation quaternion only if rotation is defined and has changed since last frame
        if (rotation) {
            if (rotation[0] !== prevRotationRef.current?.[0] || rotation[1] !== prevRotationRef.current?.[1] || rotation[2] !== prevRotationRef.current?.[2]) {
                prevRotationRef.current = [...rotation];
                objectEuler.set(...rotation, 'XYZ');
                userQuat.setFromEuler(objectEuler);
            }
        }
        // Reset camera position cache when isCameraOrbiting changes
        if (prevCameraOrbitingRef.current !== isCameraOrbiting) {
            prevCameraOrbitingRef.current = isCameraOrbiting;
            cameraPositionCachedRef.current = false;
        }
        // Get parent world quaternion inverse
        const parent = obj.parent;
        if (parent) {
            parent.getWorldQuaternion(parentQuatInv);
            parentQuatInv.invert();
        }
        else {
            parentQuatInv.identity();
        }
        // if (!isCameraOrbiting) {
        obj.getWorldPosition(worldPos);
        if (isCameraOrbiting) {
            camera.getWorldPosition(cameraWorldPosition);
        }
        else if (!cameraPositionCachedRef.current) {
            // Camera world position is fixed when not orbiting, only fetch once
            camera.getWorldPosition(cameraWorldPosition);
            cameraPositionCachedRef.current = true;
        }
        cameraUpVector.set(0, 1, 0).applyQuaternion(camera.quaternion);
        lookAtMatrix.lookAt(cameraWorldPosition, worldPos, cameraUpVector);
        billboardQuat.setFromRotationMatrix(lookAtMatrix);
        if (lockY) {
            // Isolate purely the Y-rotation (Twist-Swing Decomposition)
            // This safely bypasses Gimbal Lock and vector-length zeros
            billboardQuat.set(0, billboardQuat.y, 0, billboardQuat.w).normalize();
            // Cancel only parent's Y rotation
            parentEuler.setFromQuaternion(parentQuatInv, 'YXZ');
            parentQuatInv.setFromAxisAngle(upVector, parentEuler.y);
        }
        // Transform billboard rotation from world space into parent's local space
        billboardQuat.premultiply(parentQuatInv);
        // Combine billboard + user rotation
        obj.quaternion.copy(billboardQuat);
        if (rotation)
            obj.quaternion.multiply(userQuat);
    });
};
